import { http, SmartlyActionItems, SmartlyActionSubtypes } from '@Techedge/digitalportal-shared'
import { DelegatePayload, DelegateType } from '../models/assignee'
import { ProjectApiCreateActivityBody, TaskPhotoUpload } from '../models/projects-service'
import { TaskSubType } from '../models/task'

export enum UploadFileType {
    PHOTO = 'photo',
    FILE = 'file',
    SPLIT_BY_ROW = 'split_by_row',
    SPLIT_BY_SHEET = 'split_by_sheet',
}

const b64toBlob = (b64Data, contentType?, sliceSize?) => {
    contentType = contentType || ''
    sliceSize = sliceSize || 512
    var byteCharacters = atob(b64Data)
    var byteArrays = []
    for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        var slice = byteCharacters.slice(offset, offset + sliceSize)
        var byteNumbers = new Array(slice.length)
        for (var i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i)
        }
        var byteArray = new Uint8Array(byteNumbers)
        byteArrays.push(byteArray)
    }
    var blob = new Blob(byteArrays, { type: contentType })
    return blob
}

export class ProjectsService {
    static BASE_URL: string = process.env.REACT_APP_BASE_URL_BE
    static PROJECTS_URL: string = ProjectsService.BASE_URL + `/projectsapi`
    static ATTACHMENTS_URL: string = ProjectsService.BASE_URL + `/attachmentsapi`
    static FROALA_UPLOAD_URL = `${ProjectsService.ATTACHMENTS_URL}/froala/upload`
    static SEARCH_API_URL: string = ProjectsService.BASE_URL + `/searchapi`
    static GALLERY_BASE_URL: string = ProjectsService.BASE_URL + `/galleryapi`
    static FILTERS_BASE_URL: string = ProjectsService.BASE_URL + `/filtersapi`
    static GRSD_URL: string = ProjectsService.BASE_URL + `/svtapi/user/refresh_token`

    static headersObj = {
        headers: {
            'Accept-Language': '',
        },
        timeout: 180000,
    }

    static GetSurveyList = (banner: string[], region: string[], country: string[]): any => {
        return http.get<any>(
            `${ProjectsService.PROJECTS_URL}/surveys?banner=${encodeURIComponent(
                JSON.stringify(banner)
            )}&country=${encodeURIComponent(JSON.stringify(country))}&type=SURVEY`,
            {
                ...ProjectsService.headersObj,
                // params: { banner, region, country, type: 'SURVEY' },
            }
        )
    }

    static GetFormList = (banner: string[], region: string[], country: string[]): any => {
        return http.get<any>(
            `${ProjectsService.PROJECTS_URL}/surveys?banner=${encodeURIComponent(
                JSON.stringify(banner)
            )}&region=${encodeURIComponent(JSON.stringify(region))}&country=${encodeURIComponent(
                JSON.stringify(country)
            )}&type=FORM`,
            {
                ...ProjectsService.headersObj,
                // params: { banner, region, country, type: 'FORM' },
            }
        )
    }

    static GetProjectDetails = (projectId: string, stores?: string[]): any => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/${projectId}`,
            { stores },
            ProjectsService.headersObj
        )
    }

    static CreateActivity = (activity: ProjectApiCreateActivityBody): any => {
        return http.post<any>(
            `${ProjectsService.PROJECTS_URL}/activities`,
            activity,
            ProjectsService.headersObj
        )
    }

    static ReleaseProject = (projectId: string) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/release/${projectId}`,
            {},
            ProjectsService.headersObj
        )
    }

    static RejectProject = (projectId: string, message: string) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/reject/${projectId}`,
            { message },
            ProjectsService.headersObj
        )
    }

    static ApproveStore = (projectId: string, storeId: string) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/send_in_approval/${projectId}`,
            { storeId },
            ProjectsService.headersObj
        )
    }

    static CloseProject = (projectId: string, storeIds?: string[]) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/close/${projectId}`,
            { storeIds },
            ProjectsService.headersObj
        )
    }

    static CancelProject = (projectId: string, body?: { macroreason: string; reason: string }) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/cancel/${projectId}`,
            body || {},
            ProjectsService.headersObj
        )
    }
    static RecallProject = (projectId: string) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/recall/${projectId}`,
            {},
            ProjectsService.headersObj
        )
    }

    static MarkAsReadProject = (projectId: string, isRead: boolean) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/mark_as_read/${projectId}`,
            { isRead },
            ProjectsService.headersObj
        )
    }

    static SaveAsToBeReleased = (projectId: string) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/to_be_released/${projectId}`,
            {},
            ProjectsService.headersObj
        )
    }

    static AnswerQuestion = (
        projectId: string,
        questionId: string,
        storeId: string,
        answer: string,
        jobId?: string,
        answerAttachments?: any[]
    ) => {
        let body: any = {
            projectId: projectId,
            questionId: questionId,
            storeId: storeId,
            answer: answer,
            attachments: !!answerAttachments
                ? answerAttachments.map((attachment) => ({
                      _id: attachment.id || attachment._id,
                  }))
                : null,
        }
        if (jobId) body.jobId = jobId
        return http.post<any>(
            `${ProjectsService.PROJECTS_URL}/activities/answer`,
            body,
            ProjectsService.headersObj
        )
    }

    static RedoStore = (
        projectId: string,
        storeIds: string[],
        questionsReasons?: string,
        tasksReasons?: string,
        surveysReasons?: string
    ) => {
        let body: any = { storeIds }
        if (!!questionsReasons) body.questionsReasons = questionsReasons
        if (!!tasksReasons) body.jobsReasons = tasksReasons
        if (!!surveysReasons) body.surveysReasons = surveysReasons
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/redo/${projectId}`,
            body,
            ProjectsService.headersObj
        )
    }

    static HistoryChange = (newProject: any) => {
        return http.post<any>(
            `${ProjectsService.PROJECTS_URL}/preview/changelog`,
            { newProject },
            ProjectsService.headersObj
        )
    }

    static EditProject = (
        newProject: any,
        reasonChoice: string[],
        reasonChange: string,
        messageStores: string,
        statusAction?: string
    ) => {
        let body: any = { newProject, statusAction }
        if (reasonChange) body.reasonChange = reasonChange
        if (reasonChoice) body.reasonChoice = reasonChoice
        if (messageStores) body.messageStores = messageStores
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/edit`,
            body,
            ProjectsService.headersObj
        )
    }

    static editFile = (
        file: any,
        type: UploadFileType,
        fileName: string,
        contentType: string,
        id: string
    ): Promise<any> => {
        const formData = new FormData()
        var splittedB64 = file.split(',')[1]
        var blob = b64toBlob(splittedB64, contentType)
        formData.append('file', blob, fileName)
        formData.append('type', type)
        formData.append('attachmentType', 'projectattachment')
        formData.append('id', id)
        return http.put<any>(`${ProjectsService.ATTACHMENTS_URL}/project/update`, formData, {
            ...ProjectsService.headersObj,
            headers: {
                ...ProjectsService.headersObj.headers,
                'Content-Type': 'multipart/form-data',
            },
        })
    }

    static UploadFile = (
        file: any,
        type: UploadFileType,
        fileName: string,
        contentType: string,
        customEndpoint?: string,
        messageEndpoint?: string
    ): Promise<any> => {
        const formData = new FormData()
        var splittedB64 = file.split(',')[1]
        var blob = b64toBlob(splittedB64, contentType)
        formData.append('file', blob, fileName)
        formData.append('type', type)
        if (type === UploadFileType.SPLIT_BY_ROW || type === UploadFileType.SPLIT_BY_SHEET) {
            formData.append('attachmentType', type)
        } else {
            formData.append(
                'attachmentType',
                messageEndpoint || (customEndpoint || 'project') + 'attachment'
            )
        }
        return http.post<any>(
            `${ProjectsService.ATTACHMENTS_URL}/${
                customEndpoint ? customEndpoint : 'project'
            }/upload`,
            formData,
            {
                ...ProjectsService.headersObj,
                headers: {
                    ...ProjectsService.headersObj.headers,
                    'Content-Type': 'multipart/form-data',
                },
            }
        )
    }

    static TaskEmptyAnswer = (projectId: string, storeId: string, answer: string) => {
        return http.post<any>(
            `${ProjectsService.PROJECTS_URL}/activities/project_answer`,
            { projectId, storeId, answer },
            ProjectsService.headersObj
        )
    }

    static GetDelegateHistory = (params: {
        projectId: string
        storeId: string
        type: DelegateType
    }) => {
        let queryString = new URLSearchParams(params).toString()
        return http.get<any>(
            `${ProjectsService.PROJECTS_URL}/activities/delegate/history?${queryString}`,
            ProjectsService.headersObj
        )
    }

    static DelegateProject = (projectId: string, delegateBody: DelegatePayload) => {
        if (delegateBody.jobIds?.length === 0) {
            delete delegateBody.jobIds
        }
        if (delegateBody.surveyIds?.length === 0) {
            delete delegateBody.surveyIds
        }
        if (delegateBody.questionIds?.length === 0) {
            delete delegateBody.questionIds
        }
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/delegate/${projectId}`,
            delegateBody,
            ProjectsService.headersObj
        )
    }

    static SplitToken = () => {
        return http.get<any>(
            `${ProjectsService.ATTACHMENTS_URL}/splitfile/token`,
            ProjectsService.headersObj
        )
    }

    static GetApproversList = (type: TaskSubType) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/role_approvers`,
            { type },
            ProjectsService.headersObj
        )
    }
    static GetReassignExecutorRoleList = (
        projectType: SmartlyActionItems,
        mysteryVisit?: boolean
    ) => {
        return http.get<any>(
            `${ProjectsService.FILTERS_BASE_URL}/assignees/roles?project_type=${projectType}${
                mysteryVisit ? '&mystery_visit=true' : ''
            }`,
            ProjectsService.headersObj
        )
    }

    static GetSurveyStatus = (surveyId: string, storeId: string, projectId: string) => {
        let body = { surveyId: surveyId, storeId: storeId, projectId: projectId }

        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/survey/status`,
            body,
            ProjectsService.headersObj
        )
    }

    static DownloadSurveyList = (banner: string[], country: string[], type: 'SURVEY' | 'FORM') => {
        return http.put<any>(
            `${ProjectsService.SEARCH_API_URL}/export/surveys`,
            {
                banner,
                country,
                type,
            },
            {
                ...ProjectsService.headersObj,
            }
        )
    }
    static DownloadVisitSurveyList = (store_id: string) => {
        return http.put<any>(
            `${ProjectsService.SEARCH_API_URL}/export/surveys_svt`,
            {
                store_id,
                fields: [
                    {
                        name: 'surveyName',
                        position: 1,
                    },
                    {
                        name: 'duration',
                        position: 2,
                    },
                ],
            },
            {
                ...ProjectsService.headersObj,
            }
        )
    }

    static GetActivityTaskPhoto = (projectId: string, storeId: string, params) => {
        return http.get<any>(
            `${ProjectsService.GALLERY_BASE_URL}/project/${projectId}/attachments/${storeId}`,
            {
                params,
                ...ProjectsService.headersObj,
            }
        )
    }
    static UploadActivityTaskPhoto = (photos: TaskPhotoUpload[]) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/upload_photo_task`,
            { photos },
            {
                ...ProjectsService.headersObj,
            }
        )
    }
    static DeleteActivityTaskPhoto = (attachmentIds: string[]) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/delete_photo_task`,
            { attachmentIds },
            {
                ...ProjectsService.headersObj,
            }
        )
    }
    static AcceptPhotographicTask = (projectId: string, storeId: string) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/accept_execution_task`,
            { storeIdList: [storeId], projectId },
            {
                ...ProjectsService.headersObj,
            }
        )
    }
    static AcceptApprovalPhotographicTask = (projectId: string, storeIdList: string[]) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/accept_approval_task`,
            { storeIdList, projectId },
            {
                ...ProjectsService.headersObj,
            }
        )
    }
    static ApprovePhoto = (attachmentIds: string[]) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/photos/approve`,
            {
                attachmentIds,
            },
            {
                ...ProjectsService.headersObj,
            }
        )
    }
    static RejectPhoto = (attachmentIds: string[]) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/photos/reject`,
            {
                attachmentIds,
            },
            {
                ...ProjectsService.headersObj,
            }
        )
    }
    static PublishPhoto = (attachmentIds: string[]) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/photos/publish`,
            {
                attachmentIds,
            },
            {
                ...ProjectsService.headersObj,
            }
        )
    }
    static SendInApprovalPhoto = (attachmentIds: string[]) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/photos/send_in_approval`,
            {
                attachmentIds,
            },
            {
                ...ProjectsService.headersObj,
            }
        )
    }

    static SearchProject = (
        projectTitle: string,
        projectType?: string,
        fields?: string[],
        storeId?: string,
        controller?: AbortController
    ) => {
        const module = projectType ? `&module={"value":["${projectType}"]}` : ''
        const fieldsQueryString = fields
            ? `&fields={"value":[${fields.map((f) => '"' + f + '"').join(',')}]}`
            : ''
        const storeIdQueryString = storeId ? `&storeIds=${storeId}` : ''

        return http.get<any>(
            `${ProjectsService.SEARCH_API_URL}/activitylist?project_title={"value":"${projectTitle}"}${module}${fieldsQueryString}${storeIdQueryString}`,
            {
                ...ProjectsService.headersObj,
                ...(controller ? { signal: controller.signal } : {}),
            }
        )
    }

    static GetProjectNames = (projectIds: string[]) => {
        const idsQueryString = `id={"value":[${projectIds.map((f) => '"' + f + '"').join(',')}]}`
        const fieldsQueryString = `&fields={"value":["id", "project_name"]}`

        return http.get<any>(
            `${ProjectsService.SEARCH_API_URL}/activitylist?${idsQueryString}${fieldsQueryString}`,
            {
                ...ProjectsService.headersObj,
            }
        )
    }
    static GetGRSDToken = () => {
        return http.get<any>(ProjectsService.GRSD_URL, {
            ...ProjectsService.headersObj,
        })
    }

    static DeletePhoto = (attachmentIds: string[]) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/delete_photo_task`,
            { attachmentIds },
            {
                ...ProjectsService.headersObj,
            }
        )
    }
    static GetVisitSurveyList = (storeId: string) => {
        return http.get<any>(`${ProjectsService.PROJECTS_URL}/surveys_svt?storeId=${storeId}`, {
            ...ProjectsService.headersObj,
        })
    }

    static GetLastVisits = (body: { storeIds: string[]; surveyIds?: string[] }) => {
        return http.put<any>(
            `${ProjectsService.PROJECTS_URL}/activities/last`,
            { projectType: SmartlyActionItems.VISIT, ...body },
            {
                ...ProjectsService.headersObj,
            }
        )
    }
}
